import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { listCompaniesToReview, listFlags } from "../../api/admin";
import type { Flag } from "../../api/admin";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Sheet } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { nl } from "../../i18n/nl";
import { formatAgo } from "../../lib/format";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { AuditList } from "./AuditList";
import { FlagDetail } from "./FlagDetail";
import styles from "./moderation.module.css";
import { flagsKey, REVIEW_KEY, useOpenFlags } from "./queries";
import { ReviewList } from "./ReviewList";

const t = nl.admin.moderation;
type Tab = "open" | "resolved" | "review" | "audit";
const TABS: readonly Tab[] = ["open", "resolved", "review", "audit"];

function targetName(flag: Flag): string {
  return flag.report?.company_name ?? flag.company?.name ?? flag.user?.email ?? t.targets[flag.target_type];
}

function QueueRow({ flag, selected, onSelect }: { flag: Flag; selected: boolean; onSelect: () => void }) {
  return (
    <li data-flag-id={flag.id}>
      <button
        type="button"
        className={[styles.queueRow, selected && styles.selected].filter(Boolean).join(" ")}
        aria-current={selected ? "true" : undefined}
        onClick={onSelect}
      >
        <span className={styles.queueTop}>
          <span className={styles.reason}>{t.reasons[flag.reason]}</span>
          <span className={styles.meta}>{formatAgo(flag.resolved_at ?? flag.created_at)}</span>
        </span>
        <span className={styles.queueTitle}>{targetName(flag)}</span>
        <span className={styles.meta}>
          {t.targets[flag.target_type]} ·{" "}
          {flag.action ? t.actions[flag.action] : flag.reporter ? flag.reporter.initials : t.automatic}
        </span>
      </button>
    </li>
  );
}

/** Rows are buttons; ↑/↓ moves the selection (and focus) like the company list. */
function Queue({ flags, shownId, onSelect }: { flags: Flag[]; shownId: string | null; onSelect: (id: string) => void }) {
  const listRef = useRef<HTMLUListElement>(null);
  const latest = useRef({ flags, shownId, onSelect });
  latest.current = { flags, shownId, onSelect };

  // A native listener: the rows are the interactive elements, the list only routes ↑/↓.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const { flags: items, shownId: shown, onSelect: select } = latest.current;
      const focused = (event.target as HTMLElement).closest<HTMLElement>("[data-flag-id]");
      const current = items.findIndex((f) => f.id === (focused?.dataset.flagId ?? shown));
      const next = event.key === "ArrowDown" ? current + 1 : current - 1;
      const target = items[Math.max(0, Math.min(items.length - 1, next))];
      if (!target || !list) return;
      event.preventDefault();
      select(target.id);
      list.querySelector<HTMLElement>(`[data-flag-id="${target.id}"] button`)?.focus();
    }
    list.addEventListener("keydown", onKeyDown);
    return () => {
      list.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <ul ref={listRef} className={styles.queue} aria-label={t.queueLabel}>
      {flags.map((flag) => (
        <QueueRow
          key={flag.id}
          flag={flag}
          selected={flag.id === shownId}
          onSelect={() => {
            onSelect(flag.id);
          }}
        />
      ))}
    </ul>
  );
}

function Rules() {
  return (
    <section className={styles.rules} aria-labelledby="moderation-rules">
      <h2 id="moderation-rules" className={styles.panelTitle}>
        {t.rulesTitle}
      </h2>
      <ol className={styles.rulesList}>
        {t.rules.map((rule) => (
          <li key={rule.title}>
            <strong>{rule.title}</strong> {rule.text}
          </li>
        ))}
      </ol>
    </section>
  );
}

function FlagQueue({ state }: { state: "open" | "resolved" }) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const open = useOpenFlags(state === "open");
  const resolved = useQuery({
    queryKey: flagsKey("resolved"),
    queryFn: () => listFlags("resolved"),
    enabled: state === "resolved",
  });
  const flags = state === "open" ? open : resolved;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (flags.isPending) {
    return (
      <div className={styles.skeletons} aria-hidden="true">
        <Skeleton height={72} />
        <Skeleton height={72} />
        <Skeleton height={72} />
      </div>
    );
  }
  if (flags.isError) {
    return (
      <EmptyState title={t.loadError} action={<Button onClick={() => void flags.refetch()}>{nl.common.retry}</Button>} />
    );
  }
  if (flags.data.length === 0) return <EmptyState title={t.empty[state]} />;

  // On desktop the first flag is open by default; a handled flag leaves the queue, the next one shows.
  const chosen = flags.data.find((f) => f.id === selectedId) ?? null;
  const shown = chosen ?? (desktop ? (flags.data[0] ?? null) : null);

  return (
    <div className={styles.split}>
      <div className={styles.queueColumn}>
        <Queue flags={flags.data} shownId={shown?.id ?? null} onSelect={setSelectedId} />
        {desktop && <p className={styles.meta}>{t.keyboardHint}</p>}
        <Rules />
      </div>
      {desktop ? (
        <div className={styles.detailPane}>{shown ? <FlagDetail key={shown.id} flag={shown} /> : <p>{t.pick}</p>}</div>
      ) : (
        <Sheet
          open={chosen !== null}
          onOpenChange={(next) => {
            if (!next) setSelectedId(null);
          }}
          title={chosen ? targetName(chosen) : ""}
        >
          {chosen && <FlagDetail key={chosen.id} flag={chosen} />}
        </Sheet>
      )}
    </div>
  );
}

export function ModerationPage() {
  const [tab, setTab] = useState<Tab>("open");
  const open = useOpenFlags();
  const review = useQuery({ queryKey: REVIEW_KEY, queryFn: listCompaniesToReview });
  const counts: Partial<Record<Tab, number>> = {
    ...(open.data ? { open: open.data.length } : {}),
    ...(review.data ? { review: review.data.length } : {}),
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{nl.screens.moderation}</h1>
      <p className={styles.lead}>{t.lead}</p>
      <div className={styles.tabs} role="group" aria-label={t.tabsLabel}>
        {TABS.map((option) => (
          <Chip
            key={option}
            active={tab === option}
            {...(counts[option] !== undefined ? { count: counts[option] } : {})}
            onClick={() => {
              setTab(option);
            }}
          >
            {t.tabs[option]}
          </Chip>
        ))}
      </div>
      {(tab === "open" || tab === "resolved") && <FlagQueue key={tab} state={tab} />}
      {tab === "review" && <ReviewList />}
      {tab === "audit" && <AuditList />}
    </div>
  );
}
